import handleError from '@fuse/core/errorHandler';
import { Search } from '@mui/icons-material';
import { Box, Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useGetTemplateCategoriesQuery,
  useLazyGetFilterTemplatesCategoryQuery,
  useLazyGetTemplatesFieldsQuery,
  useLazyGetTemplatesQuery,
  useLazyGetTemplateSubCategoriesByIdQuery,
} from 'src/app/services/claim/claimApi';

export default function CreateDocumentScreen() {
  const { t } = useTranslation('translations');
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [levels, setLevels] = useState([]);
  const [templatesByCategory, setTemplatesByCategory] = useState(null);

  const { data: categories } = useGetTemplateCategoriesQuery();
  const [triggerSubCategories] = useLazyGetTemplateSubCategoriesByIdQuery();
  const [getTemplate] = useLazyGetTemplatesQuery();
  const [getTemplateByCategory] = useLazyGetFilterTemplatesCategoryQuery();
  const [getTemplateFields] = useLazyGetTemplatesFieldsQuery();

  const reqRef = useRef(0);
  const nextReq = () => ++reqRef.current;

  const handleGetTemplates = async (id, reqId) => {
    try {
      const res = await getTemplateByCategory({ subCategoryId: id }).unwrap();
      if (reqRef.current !== reqId) return;
      setTemplatesByCategory(res);
    } catch (e) {
      if (reqRef.current !== reqId) return;
      handleError(e);
    }
  };

  const handleChangeCategory = async (categoryId) => {
    const reqId = nextReq();
    setSelectedCategory(categoryId);
    setLevels([]);
    setTemplatesByCategory(null);
    setSearch('');

    try {
      const res = await triggerSubCategories({ categoryId }).unwrap();
      if (reqRef.current !== reqId) return;

      if (!res.length) {
        await handleGetTemplates(categoryId, reqId);
        return;
      }
      setLevels([{ options: res, selected: null }]);
    } catch (e) {
      if (reqRef.current !== reqId) return;
      handleError(e);
    }
  };

  const handleSelectLevel = async (pickedId, levelIndex) => {
    const reqId = nextReq();
    setTemplatesByCategory(null);
    setSearch('');

    const updated = [...levels];
    updated[levelIndex].selected = pickedId;
    updated.splice(levelIndex + 1);
    setLevels(updated);

    try {
      const res = await getTemplate({ categoryId: pickedId }).unwrap();
      if (reqRef.current !== reqId) return;

      if (!res.length) {
        await handleGetTemplates(pickedId, reqId);
        return;
      }
      setLevels([...updated, { options: res, selected: null }]);
    } catch (e) {
      if (reqRef.current !== reqId) return;
      handleError(e);
    }
  };

  const handleGetTemplateFields = async (id) => {
    try {
      await getTemplateFields({ id }).unwrap();
      navigate(`/apps/documents/my-applications/doc/${id}`);
    } catch (e) {
      handleError(e);
    }
  };

  const filteredTemplates = useMemo(() => {
    if (!templatesByCategory) return [];
    const q = search.trim().toLowerCase();
    if (!q) return templatesByCategory;
    return templatesByCategory.filter((t) => t.name?.toLowerCase().includes(q));
  }, [templatesByCategory, search]);

  const showNoData = Array.isArray(templatesByCategory) && filteredTemplates.length === 0;

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
      })}
    >
      <Typography variant="h6" textAlign="center" mb={2}>
        {t('CreatingDocument')}
      </Typography>

      {/* CATEGORY BUTTONS */}
      <Box mb={2}>
        <Typography textAlign="center" color="text.secondary">
          {t('SelectCategory')}
        </Typography>

        <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1} mt={1}>
          {categories?.map((cat) => (
            <Button
              key={cat.id}
              color="info"
              variant={selectedCategory === cat.id ? 'contained' : 'outlined'}
              onClick={() => handleChangeCategory(cat.id)}
            >
              {cat.name}
            </Button>
          ))}
        </Box>
      </Box>

      {/* LEVELS */}
      <Stack spacing={2}>
        {levels.map((level, index) => (
          <Box key={index}>
            <Typography textAlign="center" color="text.secondary">
              {t('ChooseSubcategory')}
            </Typography>

            <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1} mt={1}>
              {level.options.map((item) => (
                <Button
                  key={item.id}
                  color="info"
                  variant={level.selected === item.id ? 'contained' : 'outlined'}
                  onClick={() => handleSelectLevel(item.id, index)}
                >
                  {item.name}
                </Button>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>

      {/* SEARCH */}
      {templatesByCategory && (!showNoData || search !== '') && (
        <TextField
          fullWidth
          size="small"
          placeholder={t('Search') + '...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* TEMPLATES */}
      <Box mt={2}>
        {filteredTemplates.map((template) => (
          <Button
            key={template.id}
            fullWidth
            variant="outlined"
            sx={{ mb: 1, justifyContent: 'flex-start' }}
            onClick={() => handleGetTemplateFields(template.id)}
          >
            {template.name}
          </Button>
        ))}

        {showNoData && (
          <Typography textAlign="center" color="text.secondary">
            {t('NoTemplate')}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
